import moment from "moment";
import { PAYMENT_STATUS, RELATIONSHIP } from "utils/constants";
import { toBooleanFieldValue } from "utils/helpers/booleanField";
import { getCollectionItems, RECOMMENDED_POSITION } from "./dataSources";

export const extractCollections = (htmlContent, manifest) => {
  if (!htmlContent || !manifest)
    return { html: htmlContent || "", collections: {} };
  const slots = manifest.slots || [];
  const listSlots = slots.filter(
    (s) =>
      s &&
      s.type === "list" &&
      s.binding &&
      s.binding.kind === "data" &&
      s.binding.source,
  );
  if (listSlots.length === 0) return { html: htmlContent, collections: {} };

  const isFullDoc =
    htmlContent.indexOf("<!DOCTYPE") >= 0 || htmlContent.indexOf("<html") >= 0;
  const doc = new DOMParser().parseFromString(htmlContent, "text/html");
  const collections = {};

  for (const slot of listSlots) {
    const name = slot.binding.source;
    const tpl = doc.querySelector("[data-kayros-slot='" + slot.id + "_item']");
    if (!tpl || !tpl.parentElement) continue;
    const parent = tpl.parentElement;
    collections[name] = {
      itemTemplate: tpl.outerHTML,
      slotId: slot.id,
      selection: parent.getAttribute("data-selection") || null,
      recommendedPosition:
        parent.getAttribute(RECOMMENDED_POSITION.ATTRIBUTE) || null,
      itemLists: Array.from(tpl.querySelectorAll("[data-item-list]")).map(
        (el) => el.getAttribute("data-item-list"),
      ),
    };
    const siblings = parent.querySelectorAll("[data-collection-item]");
    for (const sib of siblings) {
      if (sib !== tpl) sib.remove();
    }
    const placeholder = doc.createElement("div");
    placeholder.setAttribute("data-collection-portal", name);
    placeholder.style.display = "contents";
    parent.replaceChild(placeholder, tpl);
  }

  const html = isFullDoc
    ? "<!DOCTYPE html>" + doc.documentElement.outerHTML
    : doc.body.innerHTML;
  return { html, collections };
};

export const resolveFieldPath = (data, path) => {
  if (!data || !path) return undefined;
  const parts = path.split(".");
  let current = data;
  for (const part of parts) {
    if (current === null || current === undefined) return undefined;
    current = current[part];
  }
  return current;
};

export const computeAge = (birthDate) => {
  if (!birthDate) return undefined;
  const birth = moment(birthDate);
  if (!birth.isValid()) return undefined;
  return String(moment().diff(birth, "years"));
};

export const currentSubscriptionOf = (project) => {
  const subs = project.subscriptions;
  const currentSubId = project.fields && project.fields.currentSubscription;
  return currentSubId && subs
    ? subs.find((s) => s.id === currentSubId)
    : subs && subs[0];
};

const PROJECT_FIELDS_PREFIX = "fields.";

const resolveProjectValue = (project, property) => {
  if (property.indexOf(PROJECT_FIELDS_PREFIX) !== 0)
    return resolveFieldPath(project, property);
  const key = property.slice(PROJECT_FIELDS_PREFIX.length);
  const fields = project.fields || {};
  return Object.prototype.hasOwnProperty.call(fields, key)
    ? fields[key]
    : resolveFieldPath(project, property);
};

export const resolveStoredValue = (project, entity, property) => {
  if (!project || !property) return undefined;

  if (entity === "user" && property === "age") {
    const birthDate = resolveFieldPath(project, "contact.user.birthDate");
    return computeAge(birthDate);
  }
  if (entity === "spouse" && property === "age") {
    const relatedPersons =
      resolveFieldPath(project, "contact.relatedPersons") || [];
    const spouse = relatedPersons.find(
      (p) => p.relationship === RELATIONSHIP.SPOUSE,
    );
    if (!spouse) return undefined;
    return computeAge(resolveFieldPath(spouse, "identity.user.birthDate"));
  }
  if (entity === "contact" && property === "hasSpouse") {
    const relatedPersons =
      resolveFieldPath(project, "contact.relatedPersons") || [];
    return toBooleanFieldValue(
      relatedPersons.some((p) => p.relationship === RELATIONSHIP.SPOUSE),
    );
  }
  if (entity === "contact" && property === "hasChildren") {
    const relatedPersons =
      resolveFieldPath(project, "contact.relatedPersons") || [];
    return toBooleanFieldValue(
      relatedPersons.some((p) => p.relationship === RELATIONSHIP.CHILD),
    );
  }

  if (entity === "project") return resolveProjectValue(project, property);
  if (entity === "user")
    return resolveFieldPath(project, "contact.user." + property);
  if (entity === "contact")
    return resolveFieldPath(project, "contact." + property);
  if (entity === "contract")
    return resolveFieldPath(project, "contract." + property);
  if (entity === "advanced") {
    const fromAdvanced = resolveFieldPath(project, "advanced." + property);
    if (fromAdvanced !== undefined && fromAdvanced !== "") {
      return fromAdvanced;
    }
    const paid =
      resolveFieldPath(project, "fields.paymentStatus") === PAYMENT_STATUS.PAID;
    const isQuotePrice =
      property === "selectedPlanPrice" || property === "selectedPlanBasePrice";
    if (!paid && isQuotePrice) {
      return fromAdvanced;
    }
    const fromFields = resolveFieldPath(project, "fields." + property);
    if (fromFields !== undefined && fromFields !== "") return fromFields;
    if (property === "selectedPlanPrice") {
      return resolveFieldPath(project, "fields.quotedPremiumFormatted");
    }
    if (property === "selectedPlanBasePrice") {
      return resolveFieldPath(project, "fields.quotedBasePremiumFormatted");
    }
    return fromFields;
  }
  if (entity === "custom") {
    return resolveFieldPath(project, "fields." + property);
  }
  if (entity === "telecom") {
    const telecoms = resolveFieldPath(project, "contact.telecoms") || [];
    const system =
      property.indexOf("value.") === 0
        ? property.slice("value.".length)
        : String(property || "").toUpperCase();
    const row = telecoms.find((item) => item && item.system === system);
    return row ? row.value : undefined;
  }
  if (entity === "spouse") {
    const relatedPersons =
      resolveFieldPath(project, "contact.relatedPersons") || [];
    const spouse = relatedPersons.find(
      (p) => p.relationship === RELATIONSHIP.SPOUSE,
    );
    if (!spouse) return undefined;
    return (
      resolveFieldPath(spouse, property) ||
      resolveFieldPath(spouse, "identity.user." + property) ||
      resolveFieldPath(spouse, "identity." + property)
    );
  }
  if (entity === "subscription") {
    const sub = currentSubscriptionOf(project);
    if (!sub) return undefined;
    return resolveFieldPath(sub, property);
  }
  if (entity === "payment") {
    const sub = currentSubscriptionOf(project);
    if (!sub) return undefined;
    return resolveFieldPath(sub, `payment.${property}`);
  }
  return resolveFieldPath(project, property);
};

export const resolveEntityValue = (project, entity, property) => {
  const draft =
    project && project.fields
      ? project.fields[entity + "." + property]
      : undefined;
  // Pending keystrokes / calendar picks live in project.fields and must win
  // over the last flushed entity column, otherwise a re-hydrate restores the
  // previous date while the user is changing it.
  if (draft !== undefined && draft !== null) return draft;
  return resolveStoredValue(project, entity, property);
};

export const resolveEntityId = (project, entity) => {
  if (!project) return null;
  if (entity === "project") return project.id;
  if (entity === "user") {
    const user = project.contact && project.contact.user;
    return user && user.id;
  }
  if (entity === "contact") {
    const contact = project.contact;
    return contact && contact.id;
  }
  if (entity === "contract") {
    const contract = project.contract;
    return contract && contract.id;
  }
  if (entity === "subscription") {
    const sub = currentSubscriptionOf(project);
    return sub && sub.id;
  }
  return project.id;
};

const collectEntities = (project, store) => {
  if (!store) return [];
  if (store.kind === "advanced") {
    const advanced = project.advanced || {};
    const items = store.path ? advanced[store.path] : advanced;
    return Array.isArray(items) ? items : [];
  }
  if (store.kind === "relatedPerson") {
    const all = resolveFieldPath(project, "contact.relatedPersons") || [];
    return store.relationship
      ? all.filter((p) => p && p.relationship === store.relationship)
      : all;
  }
  if (store.kind === "attachment") {
    const subs = project.subscriptions || [];
    const currentSubId = project.fields && project.fields.currentSubscription;
    const sub = currentSubId
      ? subs.find((s) => s.id === currentSubId)
      : subs[0];
    return (sub && sub.attachments) || [];
  }
  return [];
};

const buildEntityItems = (project, registryEntry) => {
  if (!registryEntry || !registryEntry.store) return [];
  const entities = collectEntities(project, registryEntry.store);
  const fields = registryEntry.fields || [];
  const childrenFields = registryEntry.childrenFields || [];

  return entities.map((entity) => {
    const item = { id: entity.id };
    if (registryEntry.store.kind === "relatedPerson" && entity.relationship) {
      item.relationship = entity.relationship;
    }
    for (const field of fields) {
      const property = field.target && field.target.property;
      if (!property) continue;
      const value = resolveFieldPath(entity, property);
      if (value !== undefined) item[field.key] = value;
    }
    for (const cf of childrenFields) {
      if (item[cf.key] !== undefined) continue;
      const value = resolveFieldPath(entity, cf.key);
      if (value !== undefined) item[cf.key] = value;
    }
    return item;
  });
};

export const projectWithLocalCollections = (
  project,
  localCollections,
  names,
) => {
  const fields = { ...((project && project.fields) || {}) };
  for (const name of names || []) {
    if (Array.isArray(localCollections && localCollections[name])) {
      fields[name] = localCollections[name];
    }
  }
  return { ...(project || {}), fields };
};

export const resolveCollectionItems = (project, registryEntry) => {
  if (!project || !registryEntry) return [];
  const entityItems = buildEntityItems(project, registryEntry);
  const buffer = project.fields && project.fields[registryEntry.key];
  if (Array.isArray(buffer)) {
    const storeKind = registryEntry.store && registryEntry.store.kind;
    if (!buffer.length && storeKind === "attachment") return entityItems;
    const byId = new Map();
    for (const item of entityItems) {
      if (!item || !item.id) continue;
      byId.set(item.id, item);
    }
    return buffer
      .filter((item) => item && item.id)
      .map((item) => ({
        ...(byId.get(item.id) || {}),
        ...item,
      }));
  }
  return entityItems;
};

export const isPlanScopedList = (slot) =>
  !!(
    slot &&
    slot.binding &&
    slot.binding.kind === "data" &&
    slot.binding.scope === "plan"
  );

export const findCollectionListSlot = (slots, name) =>
  (slots || []).find(
    (slot) =>
      slot &&
      slot.type === "list" &&
      slot.binding &&
      slot.binding.source === name,
  );

export const listAttachmentTypes = (slot) => {
  const types = slot && slot.binding && slot.binding.types;
  return Array.isArray(types) && types.length ? types : null;
};

export const resolveSelectedPlanId = (project) => {
  if (!project) return "";
  const fromFields = project.fields && project.fields.selectedPlan;
  if (fromFields) return String(fromFields);
  const subscriptions = project.subscriptions || [];
  const currentSubId = project.fields && project.fields.currentSubscription;
  const subscription = currentSubId
    ? subscriptions.find((item) => item && item.id === currentSubId)
    : subscriptions[0];
  const planId =
    subscription && subscription.insurancePlan && subscription.insurancePlan.id;
  return planId ? String(planId) : "";
};

export const toDocumentCollectionItems = (attachments) =>
  (attachments || [])
    .filter((item) => item && item.id)
    .map((item) => ({
      id: item.id,
      name: item.name || item.filename || item.id,
      filename: item.filename,
      fileUrl: item.fileUrl,
      contentType: item.contentType,
      type: item.type,
    }));

export const resolveDownloadTracking = (slots) => {
  const slot = (slots || []).find(
    (item) =>
      item &&
      item.type === "list" &&
      item.binding &&
      item.binding.trackDownloads,
  );
  if (!slot) return null;
  return {
    collection: slot.binding.source,
    downloadedField:
      slot.binding.downloadedField || "contractDocumentsDownloaded",
    downloadedIdsField:
      slot.binding.downloadedIdsField || "downloadedDocumentIds",
  };
};

export const parseDownloadedIds = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean).map(String);
  }
  if (typeof value !== "string" || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [];
  } catch (_error) {
    return [value];
  }
};

export const mergeDownloadedIds = (current, itemIds) => {
  const next = new Set(parseDownloadedIds(current));
  for (const id of itemIds || []) {
    if (id) next.add(String(id));
  }
  return Array.from(next);
};

export const collectionNamesToHydrate = (slots, extractedCollections) => {
  const names = new Set(Object.keys(extractedCollections || {}));
  for (const slot of slots || []) {
    const binding = slot && slot.binding;
    if (slot && slot.type === "list" && binding && binding.source) {
      names.add(binding.source);
    }
    if (
      binding &&
      binding.kind === "download_collection" &&
      binding.collection
    ) {
      names.add(binding.collection);
    }
  }
  return Array.from(names);
};

export const resolveHydratedCollectionItems = ({
  name,
  project,
  registry,
  listSlot,
  planAttachments,
  statusAttachments,
  ctx,
}) => {
  if (isPlanScopedList(listSlot)) {
    return toDocumentCollectionItems(planAttachments);
  }
  const entry = (registry || []).find((group) => group && group.key === name);
  if (entry) {
    const items = resolveCollectionItems(project, entry);
    if (items.length) return items;
    if (entry.store && entry.store.kind === "attachment") {
      return toDocumentCollectionItems(statusAttachments);
    }
    const fromSource = getCollectionItems(name, ctx || {});
    if (fromSource.length) return fromSource;
    return items;
  }
  const fromSource = getCollectionItems(name, ctx || {});
  if (fromSource.length) return fromSource;
  const buffered = project && project.fields && project.fields[name];
  if (Array.isArray(buffered)) return buffered;
  return fromSource;
};

export const resolveDownloadCollectionItems = ({
  binding,
  collections,
  project,
  registry,
  statusAttachments,
}) => {
  if (!binding || !binding.collection) return [];
  const fromState = filterCollectionItemsByTypes(
    downloadableCollectionItems(
      (collections && collections[binding.collection]) || [],
    ),
    binding.types,
  );
  if (fromState.length) return fromState;
  const group = (registry || []).find(
    (entry) => entry && entry.key === binding.collection,
  );
  if (group) {
    const fromProject = filterCollectionItemsByTypes(
      downloadableCollectionItems(resolveCollectionItems(project, group)),
      binding.types,
    );
    if (fromProject.length) return fromProject;
  }
  return filterCollectionItemsByTypes(
    downloadableCollectionItems(toDocumentCollectionItems(statusAttachments)),
    binding.types,
  );
};

export const collectEmptyDownloadCollectionSlotIds = ({
  slots,
  collections,
  project,
  registry,
  statusAttachments,
}) =>
  (slots || [])
    .filter((slot) => {
      const binding = slot && slot.binding;
      if (!slot || slot.type !== "action") return false;
      if (!binding || binding.kind !== "download_collection") return false;
      return !resolveDownloadCollectionItems({
        binding,
        collections,
        project,
        registry,
        statusAttachments,
      }).length;
    })
    .map((slot) => slot.id);

export const filterCollectionItemsByTypes = (items, types) => {
  if (!Array.isArray(types) || !types.length) return items || [];
  const allowed = new Set(types.map(String));
  return (items || []).filter((item) => item && allowed.has(String(item.type)));
};

export const downloadableCollectionItems = (items) =>
  (items || []).filter((item) => item && item.id);

export const collectionDownloadQuery = (items) => {
  const ids = (items || [])
    .map((item) => item && item.id)
    .filter(Boolean)
    .map(String);
  if (!ids.length) return null;
  return { data: { isIn: { id: ids } } };
};

export const collectionZipName = (binding) => {
  const name = binding && binding.zipName;
  if (typeof name === "string" && name.trim()) return name.trim();
  return "documents";
};

export const shouldZipCollectionDownload = (items) =>
  downloadableCollectionItems(items).length > 1;

export const areCollectionDownloadsComplete = (items, downloadedIds) => {
  const required = downloadableCollectionItems(items).map((item) =>
    String(item.id),
  );
  if (!required.length) return false;
  const downloaded = new Set(parseDownloadedIds(downloadedIds));
  return required.every((id) => downloaded.has(id));
};

export const SCREEN_IFRAME_SANDBOX = [
  "allow-same-origin",
  "allow-scripts",
  "allow-forms",
  "allow-popups",
  "allow-popups-to-escape-sandbox",
].join(" ");

export const SCREEN_IFRAME_VIEWPORT_META =
  '<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0" />';

export const DATE_FIELD_ATTRIBUTE = "data-krs-date-field";

export const DATE_NATIVE_ATTRIBUTE = "data-krs-date-native";

export const DATE_EDITING_ATTRIBUTE = "data-krs-date-editing";

export const DATE_VALUE_EVENT = "krs-date-value";

export const SCREEN_IFRAME_SLOT_CHROME =
  "<style>" +
  `[${DATE_NATIVE_ATTRIBUTE}]{display:none!important;}` +
  `[${DATE_FIELD_ATTRIBUTE}]{display:contents;}` +
  ".krs-date-frame{position:relative;display:flex;align-items:center;}" +
  ".krs-date-hint{position:absolute;left:0;bottom:calc(100% + 10px);z-index:10;" +
  "display:flex;align-items:center;gap:8px;width:max-content;max-width:min(320px,100%);" +
  "padding:8px 12px;border-radius:24px;background:#333333;color:#ffffff;" +
  "font-size:12px;font-weight:400;line-height:1.2;text-align:left;pointer-events:none;}" +
  ".krs-date-hint:before{content:'';position:absolute;left:24px;bottom:-8px;" +
  "border-left:8px solid transparent;border-right:8px solid transparent;border-top:8px solid #333333;}" +
  ".krs-date-hint svg{flex:none;}" +
  "body:has(.ant-picker-dropdown[class*='-placement-top']:not(.ant-picker-dropdown-hidden)) .krs-date-hint{" +
  "top:calc(100% + 10px);bottom:auto;}" +
  "body:has(.ant-picker-dropdown[class*='-placement-top']:not(.ant-picker-dropdown-hidden)) .krs-date-hint:before{" +
  "top:-8px;bottom:auto;border-top:0;border-bottom:8px solid #333333;}" +
  ".krs-date-frame .ant-picker{flex:1;min-width:0;padding:0;background:transparent;}" +
  ".krs-date-frame .ant-picker-input>input{font:inherit;color:inherit;}" +
  "html,body{height:100%;margin:0;max-width:100%;overflow-x:hidden;}" +
  "[data-kayros-slot]{outline:none!important;}" +
  "[data-kayros-slot]:not(input):not(textarea):not(select):not(button):not(a){" +
  "cursor:default!important;}" +
  "button,[type=button],[type=submit],[type=reset],[type=checkbox]," +
  "[type=radio],label,a{cursor:pointer;}" +
  "button[data-kayros-slot],[type=button][data-kayros-slot]," +
  "[type=submit][data-kayros-slot],[type=checkbox][data-kayros-slot]," +
  "[type=radio][data-kayros-slot],a[data-kayros-slot]," +
  "label[data-kayros-slot]{cursor:pointer!important;}" +
  "input[data-kayros-slot]:not([type=checkbox]):not([type=radio])," +
  "textarea[data-kayros-slot],select[data-kayros-slot]{" +
  "cursor:text!important;pointer-events:auto!important;}" +
  "button:disabled,[disabled]{cursor:not-allowed;}" +
  "</style>";

export const htmlHasViewportMeta = (html) =>
  /<meta\b[^>]*name=["']viewport["']/i.test(String(html || ""));

export const injectViewportMeta = (html) => {
  const source = String(html || "");
  if (htmlHasViewportMeta(source)) return source;
  if (/<\/head>/i.test(source)) {
    return source.replace(/<\/head>/i, `${SCREEN_IFRAME_VIEWPORT_META}</head>`);
  }
  return source;
};
